import type { SalesFunnelStage } from '@/types/sales-performance.types';

/**
 * Pixel geometry for the funnel Sankey (Figma Kim-AI 6631:79557 conversation,
 * 6701:27790 nudges). Each column is one stage: its retained node at the top and,
 * from the second column on, the conversations that dropped since the previous
 * stage in a node underneath. Kept free of React so the geometry is testable.
 */

export interface SankeyVariant {
  /** Height of the first drawn node; every other node scales from it. */
  flowHeight: number;
  /** Vertical gap between a retained node and the drop node under it. */
  dropGap: number;
  chartHeight: number;
  /** Opacity of each retained band, first band first. */
  movedOpacity: number[];
  dropFill: string;
  dropOpacity: number;
  /** Colour of the stage name under each count. */
  labelTone: string;
}

export const CONVERSATION_SANKEY: SankeyVariant = {
  flowHeight: 280, dropGap: 22, chartHeight: 306, movedOpacity: [0.55, 0.55, 0.18],
  dropFill: 'var(--color-gray-500)', dropOpacity: 0.45, labelTone: 'text-gray-900',
};
export const NUDGE_SANKEY: SankeyVariant = {
  flowHeight: 250, dropGap: 18, chartHeight: 305, movedOpacity: [0.55, 0.55, 0.4, 0.25],
  dropFill: 'var(--color-gray-200)', dropOpacity: 1, labelTone: 'text-ink',
};

export const NODE_WIDTH = 14;
const LABEL_OFFSET = 24;
const LABEL_HEIGHT = 32;
const LABEL_SEPARATION = 36;
/** Columns sit (width + 50) / n apart, which leaves the design's tail after the last node. */
const TAIL_ALLOWANCE = 50;
/** Darkest first; a funnel with fewer columns uses the lighter end. */
const NODE_COLORS = ['indigo-700', 'indigo-600', 'indigo-500', 'indigo-400', 'indigo-300'];

export interface SankeyNode { top: number; height: number; color: string; labelTop: number; count: number; share: number; label: string }
export interface SankeyColumn { key: string; x: number; labelX: number; retained: SankeyNode; dropped: SankeyNode | null }
export interface SankeyFlow { key: string; path: string; fill: string; opacity: number }
export interface SankeyLayout { columns: SankeyColumn[]; flows: SankeyFlow[]; tail: { x: number; width: number; height: number } | null }

function band(x0: number, x1: number, sourceTop: number, sourceBottom: number, targetTop: number, targetBottom: number): string {
  const mid = (x0 + x1) / 2;
  return `M ${x0} ${sourceTop} C ${mid} ${sourceTop} ${mid} ${targetTop} ${x1} ${targetTop} V ${targetBottom} C ${mid} ${targetBottom} ${mid} ${sourceBottom} ${x0} ${sourceBottom} Z`;
}

/** Centres a two-line label on a band, never above its top edge or below the chart. */
const centredLabel = (top: number, height: number, chartHeight: number): number =>
  Math.min(chartHeight - LABEL_HEIGHT, Math.max(top, top + height / 2 - LABEL_HEIGHT / 2));

/**
 * @param entryShare when the first stage is the full cohort and is not drawn
 *   (conversation funnel), the first drawn column's share is taken against it.
 */
export function buildSankeyLayout(stages: SalesFunnelStage[], width: number, variant: SankeyVariant, hideEntry: boolean): SankeyLayout {
  const columns = hideEntry ? stages.slice(1) : stages;
  if (!columns.length) return { columns: [], flows: [], tail: null };
  const base = columns[0].count || 1;
  const entryBase = hideEntry ? stages[0].count || 1 : base;
  const scale = (value: number): number => Math.max(0, value / base * variant.flowHeight);
  const spacing = (width + TAIL_ALLOWANCE) / columns.length;
  const colors = NODE_COLORS.slice(Math.max(0, NODE_COLORS.length - columns.length));
  const drops = columns.slice(1).map((stage) => stage.dropOff);
  const biggestDrop = Math.max(0, ...drops);

  const laidOut = columns.map((stage, index): SankeyColumn => {
    const x = index * spacing;
    const height = scale(stage.count);
    const next = columns[index + 1]?.count ?? stage.count;
    const retained: SankeyNode = {
      top: 0, height, color: colors[index] ?? 'indigo-300', count: stage.count, label: stage.label,
      share: stage.count / (index === 0 ? entryBase : base) * 100,
      // Figma centres a stage's label on the band it passes on; with nothing passed on (an empty
      // next stage) that band has no height, so the label is centred on the stage's own bar.
      labelTop: scale(next) > 0
        ? Math.max(0, scale(next) / 2 - LABEL_HEIGHT / 2)
        : centredLabel(0, height, variant.chartHeight),
    };
    const hasDrop = index > 0 && stage.dropOff > 0;
    const dropTop = height + variant.dropGap;
    const dropHeight = scale(stage.dropOff);
    const dropped: SankeyNode | null = hasDrop ? {
      top: dropTop, height: dropHeight, count: stage.dropOff, label: stage.dropOffLabel ?? '',
      color: stage.dropOff === biggestDrop ? 'red-500' : 'gray-500',
      share: stage.dropOff / base * 100,
      labelTop: Math.max(centredLabel(dropTop, dropHeight, variant.chartHeight), retained.labelTop + LABEL_SEPARATION),
    } : null;
    return { key: stage.key, x, labelX: x + LABEL_OFFSET, retained, dropped };
  });

  const flows = laidOut.slice(1).flatMap((column, offset): SankeyFlow[] => {
    const from = laidOut[offset].x + NODE_WIDTH;
    const kept = column.retained.height;
    const moved: SankeyFlow = {
      key: `${column.key}-moved`, path: band(from, column.x, 0, kept, 0, kept), fill: 'var(--color-indigo-200)',
      opacity: variant.movedOpacity[offset] ?? variant.movedOpacity.at(-1) ?? 1,
    };
    if (!column.dropped) return [moved];
    const biggest = column.dropped.color === 'red-500';
    return [moved, {
      key: `${column.key}-dropped`,
      path: band(from, column.x, kept, kept + column.dropped.height, column.dropped.top, column.dropped.top + column.dropped.height),
      fill: biggest ? 'var(--color-red-100)' : variant.dropFill,
      opacity: biggest ? 1 : variant.dropOpacity,
    }];
  });

  const last = laidOut[laidOut.length - 1];
  const tailX = last.x + NODE_WIDTH;
  return { columns: laidOut, flows, tail: width > tailX ? { x: tailX, width: width - tailX, height: last.retained.height } : null };
}
